'use client'

// Client island for the city SEO pages: renders the tool funnel with links
// PRE-FILLED from the city's context (grass + central ZIP, or "City, ST" for the
// soil-temp tool) via the shared buildToolUrl convention, and fires PostHog
// tool-navigation events so city -> tool click-through is measurable (the pilot's
// make-or-break metric). The page itself stays a server component; this renders
// to crawlable HTML on first paint.

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import {
  Camera, Thermometer, FlaskConical, Droplets, Sprout, Wind, ArrowRight, type LucideIcon,
} from 'lucide-react'
import { CITY_TOOL_LINKS, cityToolHref, citySource, type CityToolLink } from '@/lib/city-seo/tool-links'
import { toolNavigationAnalytics } from '@/lib/analytics'

type ToolDef = CityToolLink & { label: string; blurb: string; icon: LucideIcon }

// Presentation metadata only -- the routes + prefill strategy + attribution live in
// CITY_TOOL_LINKS (lib/city-seo/tool-links.ts) so the funnel can't drift from its test.
const PRESENTATION: Record<string, { label: string; blurb: string; icon: LucideIcon }> = {
  diagnose: { label: 'Identify your grass from a photo', blurb: 'Free AI grass ID names it from one photo.', icon: Camera },
  'soil-temperature': { label: 'Track the 55F crabgrass trigger', blurb: 'Soil temperature for your city, so you time pre-emergent right.', icon: Thermometer },
  'fertilizer-calculator': { label: 'Get exact fertilizer amounts', blurb: 'Turn the nitrogen target into bag amounts for your lawn.', icon: FlaskConical },
  'watering-schedule': { label: 'Build a watering schedule', blurb: 'Deep, infrequent run times for your grass and soil.', icon: Droplets },
  'seeding-calculator': { label: 'Plan your overseeding', blurb: 'Seed amounts for the right window.', icon: Sprout },
  aeration: { label: 'Plan fall aeration', blurb: 'Relieve compacted soil at the right time.', icon: Wind },
}

const TOOLS: ToolDef[] = CITY_TOOL_LINKS.map((l) => ({ ...l, ...PRESENTATION[l.id] }))

// The compact early strip surfaces the 3 highest-value, most time-sensitive actions.
const QUICK_IDS = new Set(['diagnose', 'soil-temperature', 'fertilizer-calculator'])

export interface CityToolsProps {
  slug: string
  cityName: string
  state: string
  /** lead grass guide slug, e.g. "bermudagrass" */
  grass?: string
  /** central city ZIP for tool pre-fill */
  zip?: string
  placement: 'quick' | 'full'
}

export function CityTools({ slug, cityName, state, grass, zip, placement }: CityToolsProps) {
  const source = citySource(slug)
  const placementLabel = placement === 'quick' ? 'city_quick_strip' : 'city_tools_section'
  const tools = placement === 'quick' ? TOOLS.filter((t) => QUICK_IDS.has(t.id)) : TOOLS
  const viewed = useRef(false)

  useEffect(() => {
    if (viewed.current) return
    viewed.current = true
    toolNavigationAnalytics.relatedToolsViewed({ sourceTool: source, placement: placementLabel, toolCount: tools.length })
  }, [source, placementLabel, tools.length])

  const hrefFor = (t: ToolDef) => cityToolHref(t, { slug, cityName, state, grass, zip })

  const handleClick = (t: ToolDef, rank: number) =>
    toolNavigationAnalytics.relatedToolClicked({
      sourceTool: source,
      targetTool: t.id,
      rank: rank + 1,
      placement: placementLabel,
      recommendationReason: `${cityName}: ${t.label}`,
      prefillFields: t.prefill === 'location' ? 'location' : t.prefill === 'grass-zip' ? 'grass,zip' : '',
    })

  if (placement === 'quick') {
    return (
      <nav className="alm-quick" aria-label={`Quick tools for your ${cityName} lawn`}>
        {tools.map((t, i) => (
          <Link key={t.id} href={hrefFor(t)} prefetch={false} className="alm-quick__item" onClick={() => handleClick(t, i)}>
            <span className="alm-quick__ico"><t.icon size={18} aria-hidden="true" /></span>
            <span className="alm-quick__label">{t.label}</span>
            <ArrowRight size={15} aria-hidden="true" className="alm-quick__arrow" />
          </Link>
        ))}
      </nav>
    )
  }

  return (
    <div className="alm-tools">
      <header className="alm-sechead">
        <h2>Free tools for your {cityName} lawn</h2>
        <p>Turn this guide into exact numbers for your yard, pre-filled for {cityName}. No login, no sales pitch.</p>
      </header>
      <div className="alm-tools__grid">
        {tools.map((t, i) => (
          <Link key={t.id} href={hrefFor(t)} prefetch={false} className="alm-tool" onClick={() => handleClick(t, i)}>
            <span className="alm-tool__ico"><t.icon size={20} aria-hidden="true" /></span>
            <div>
              <span className="alm-tool__label">{t.label} <ArrowRight size={15} aria-hidden="true" /></span>
              <p>{t.blurb}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
